"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getFileUrl } from "@/lib/file.util";
import { Career } from "@/lib/types/career";
import { Story } from "@/lib/types/story";
import { ArrowRight, FileText } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";

interface CareerJourneyProps {
  career: Career;
  stories: Story[];
}

export default function CareerJourney({ career, stories }: CareerJourneyProps) {
  const [sortOrder, setSortOrder] = useState<string>("newest");

  // Stories explicitly linked to this career.
  const careerStories = useMemo(() => {
    return [...stories.filter((item) => item.careerId === career.id)].sort(
      (a, b) => {
        const diff =
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        return sortOrder === "newest" ? diff : -diff;
      }
    );
  }, [stories, career.id, sortOrder]);

  const hasStories = careerStories.length > 0;

  return (
    <section className="py-12">
      {hasStories && (
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Journey of a {career.title}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Explore real stories, videos, and audio from people who have pursued
            this career path in Rwanda.
          </p>
        </div>
      )}

      {hasStories ? (
        <>
          {/* Sorting */}
          <div className="flex flex-col md:flex-row justify-end items-center mb-8 gap-4">
            <div className="w-full md:w-auto">
              <Select defaultValue="newest" onValueChange={setSortOrder}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="popular">Most Popular</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Content grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {careerStories.map((content) => (
              <StoryCard key={content.id} story={content} />
            ))}
          </div>
        </>
      ) : (
        /* No linked stories yet — invite users to browse all stories. */
        <div className="w-full text-center bg-green-50 rounded-2xl p-8 md:p-10">
          <p className="text-gray-700 text-lg mb-6">
            Read inspiring journeys from healthcare professionals across Rwanda
            and discover how they built their careers.
          </p>
          <Button asChild size="lg" className="rounded-full">
            <Link href="/stories" className="inline-flex items-center gap-2">
              Explore Stories
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </section>
  );
}

// Story card component
function StoryCard({ story }: { story: Story }) {
  return (
    <Link href={`/stories/${story.id}`}>
      <Card className="overflow-hidden hover:border-primary hover:shadow-md hover:border duration-200 border-0 py-0 shadow-md">
        <div className="relative">
          <div className="aspect-video relative overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${getFileUrl(story.thumbnail)})`,
              }}
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center"></div>
          </div>
        </div>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2 text-sm text-gray-500">
            <FileText className="h-5 w-5 text-green-600" />
            <span>•</span>
            <span>
              {new Date(story.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
          <h3 className="font-bold text-lg mb-2 line-clamp-2">{story.title}</h3>
          <p className="text-gray-600 text-sm line-clamp-3">
            {story.description}
          </p>
          <Button variant="link" className="rounded-full !px-0 mt-2">
            <span className="text-primary">Read More</span>
            <ArrowRight className="h-4 w-4 text-primary" />
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
}
